// =============================================================================
// SettlementBadge — wholesale settlement status (Admin)
// =============================================================================
// Renders an order's WHOLESALE settlement state (`wholesaleChargeStatus`) —
// what Transfer Kingdom collects from the merchant — kept visually DISTINCT
// from the Shopify customer-facing payment status. Labels/tones mirror the
// merchant surfaces (portal + embedded Shopify app) so every surface reads
// identically.
// =============================================================================

import { Badge, type BadgeVariant } from '@tkpod/ui';
import { WholesaleChargeStatus } from '@tkpod/shared';

const SETTLEMENT_LABELS: Record<string, string> = {
  [WholesaleChargeStatus.NONE]: 'No charge',
  [WholesaleChargeStatus.PENDING]: 'Charge pending',
  [WholesaleChargeStatus.CHARGING]: 'Charging',
  [WholesaleChargeStatus.PAID]: 'Paid',
  [WholesaleChargeStatus.FAILED]: 'Charge failed',
  [WholesaleChargeStatus.ON_HOLD]: 'Payment on hold',
};

const SETTLEMENT_VARIANTS: Record<string, BadgeVariant> = {
  [WholesaleChargeStatus.NONE]: 'default',
  [WholesaleChargeStatus.PENDING]: 'warning',
  [WholesaleChargeStatus.CHARGING]: 'info',
  [WholesaleChargeStatus.PAID]: 'success',
  [WholesaleChargeStatus.FAILED]: 'danger',
  [WholesaleChargeStatus.ON_HOLD]: 'danger',
};

export function settlementLabel(status: string): string {
  return SETTLEMENT_LABELS[status] ?? status;
}

export function settlementVariant(status: string): BadgeVariant {
  return SETTLEMENT_VARIANTS[status] ?? 'default';
}

interface SettlementBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

export function SettlementBadge({ status, size = 'sm' }: SettlementBadgeProps) {
  return (
    <Badge variant={settlementVariant(status)} size={size}>
      {settlementLabel(status)}
    </Badge>
  );
}
